import React, { useState, useEffect, useContext, useMemo } from 'react'
import * as R from 'ramda'
import { useInvoiceAPI } from './api'
import { ACHLog, ACHStatus, ACHTransferLog, Invoice, InvoiceDetails, InvoiceTotals, Maybe } from './model'
import moment from 'moment'
import { formatCents } from './utils'
import { Redirect, withRouter, RouteChildrenProps } from 'react-router'
import { PaymentsApi } from 'views/order/v5/api/payments-api'
import { Panel, Line, Line2 } from '../order/v5/fragments/comp/panel'
import * as userStore from 'stores/user'

import oauthClient from 'api/auth'

import { ButtonToolbar, ButtonGroup, Button } from 'reactstrap'
import { Icon } from 'react-fa'

import OrdersTransactions from './order-transactions-v1'
import OrdersTransactionsV2 from './order-transactions-v2'
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap'
import { State } from 'views/order/v5/data-comp'
import { AccessGroupApis } from 'views/order/v5/api/access-groups-api'

interface InvoiceParams<idType> {
  invoiceId: idType
  invoiceType: string
}
function InvoiceDetailsUI(props: RouteChildrenProps<InvoiceParams<string>>): JSX.Element {
  const invoiceId: number = props.match ? parseInt(props.match.params.invoiceId as string) : 0
  const invoiceType = props.match ? props.match.params.invoiceType : ''

  if (!invoiceId || isNaN(invoiceId)) {
    return <Redirect to="/invoices" />
  }

  return (
    <PaymentsApi auth_token={oauthClient.getAuthResponse() ? oauthClient.getAuthResponse().access_token : null}>
      <InvoiceUI {...{ invoiceId, invoiceType }} />
    </PaymentsApi>
  )
}

function InvoiceUI(props: InvoiceParams<number>): JSX.Element {
  const invoiceAPI = useInvoiceAPI()
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [details, setDetails] = useState<InvoiceDetails | null>(null)
  // const Orders = process.env.NODE_ENV === 'production' ? OrdersTransactions : OrdersTransactionsV2
  const Orders = OrdersTransactionsV2

  useEffect(() => {
    invoiceAPI.getInvoice(props.invoiceId).then(setInvoice)
  }, [props.invoiceId])

  useEffect(() => {
    invoiceAPI.getDetails(props.invoiceId).then(setDetails)
  }, [props.invoiceId])

  if (!invoice || !details) return <>Loading...</>

  // const simplifiedInvoice = userStore.hasAnyRole('billing', 'support', 'config', 'ambassador', 'rds_admin')
  //   ? false
  //   : !!invoice.simplified_invoicing
  const simplifiedInvoice = !!invoice.simplified_invoicing
  const showIsSimplified = false
  // userStore.hasAnyRole('billing', 'support', 'config', 'ambassador', 'rds_admin') && !!invoice.simplified_invoicing

  return (
    <div className="row-fluid">
      <InvoiceHeading
        invoiceId={props.invoiceId}
        invoice={invoice}
        updateInvoice={setInvoice}
        showIsSimplified={showIsSimplified}
      />
      <StatementInfo invoice={invoice} details={details} simplifiedInvoice={simplifiedInvoice} />
      <Orders invoiceId={props.invoiceId} invoiceType={props.invoiceType} simplifiedInvoice={simplifiedInvoice} />
    </div>
  )
}

function InvoiceHeading(props: {
  invoiceId: number
  invoice: Invoice
  updateInvoice: (i: Invoice) => void
  showIsSimplified: boolean
}): JSX.Element {
  const { invoice } = props
  const invoiceAPI = useInvoiceAPI()
  const [busyEmail, setBusyEmail] = useState<boolean>(false)

  const emailInvoice = () => {
    setBusyEmail(true)
    invoiceAPI
      .download(props.invoiceId)
      .then((_: void) =>
        // doneEmail('Your request has been processed! You will get an email with the invoice content soon!')
        doneEmail(`We are processing your request. The results will be sent to you by email at ${userStore.email()}`)
      )
      .catch((_: void) => doneEmail('Could not process the request. Dev team was informed. Please retry again later.'))
  }

  const doneEmail = (msg: string): void => {
    setBusyEmail(false)
    alert(msg)
  }

  const downloadOrders = () => {
    setBusyEmail(true)
    invoiceAPI
      .downloadInvoiceOrders(props.invoiceId)
      .then((_: void) =>
        // doneEmail('Your request has been processed! You will get an email with the invoice content soon!')
        doneEmail(`We are processing your request. The results will be sent to you by email at ${userStore.email()}`)
      )
      .catch((_: void) => doneEmail('Could not process the request. Dev team was informed. Please retry again later.'))
  }

  const markAsPostponed = () => {
    if (confirm('Are you sure you want to mark this invoice as postponed?')) {
      invoiceAPI
        .updateStatus(props.invoiceId, 'payment_postponed')
        .then(props.updateInvoice)
        .catch((_: void) =>
          doneEmail('Could not process the request. Dev team was informed. Please retry again later.')
        )
    }
  }

  const markAsPaid = () => {
    if (confirm('Are you sure that this order has been paid in full')) {
      invoiceAPI
        .markPaid(props.invoiceId)
        .then(props.updateInvoice)
        .catch((_: void) =>
          doneEmail('Could not process the request. Dev team was informed. Please retry again later.')
        )
    }
  }

  return (
    <>
      <ol className="breadcrumb">
        <li key="invoices">
          <a href="#/statements">Statements</a>
        </li>
        <li key="current">
          {invoice.invoiceable_type} {invoice.invoiceable_id}
        </li>
      </ol>
      {props.showIsSimplified && (
        <h1 className="pull-right" title={'Simplified Invoicing'}>
          🍀
        </h1>
      )}
      <h1>{invoice.invoiceable_name} Statement</h1>

      <ButtonToolbar>
        <ButtonGroup>
          <Button key="email_invoice" onClick={emailInvoice} disabled={busyEmail}>
            <Icon name="email" /> Email Invoice
          </Button>
          <Button key="email_orders" onClick={downloadOrders} disabled={busyEmail}>
            <Icon name="email" /> Email Invoice Orders
          </Button>
          {userStore.hasRole('billing_admin') &&
            ['completed', 'payment_postponed', 'ach_failed'].includes(invoice.status) && (
              <Button key="mark_paid" onClick={markAsPaid}>
                <Icon name="check" /> Mark as Paid
              </Button>
            )}
          {userStore.hasRole('billing_admin') && invoice.status == 'completed' && (
            <Button key="mark_postpone" onClick={markAsPostponed}>
              <Icon name="eject" /> Mark as Postponed
            </Button>
          )}
        </ButtonGroup>
      </ButtonToolbar>
    </>
  )
}

enum StatementDetails {
  None,
  ACHStatus,
  ACHLogs,
  Credits,
  Debits,
}

function StatementInfo(props: { invoice: Invoice; details: InvoiceDetails; simplifiedInvoice: boolean }): JSX.Element {
  const { invoice, details } = props
  const invoiceAPI = useInvoiceAPI()
  const [achStatus, setACHStatus] = useState<Maybe<ACHStatus>>(null)
  const [achLog, setACHLog] = useState<Maybe<ACHLog>>(null)
  const [loadingACH, setLoadingACH] = useState(false)
  const [loadingACHLogs, setLoadingACHLogs] = useState(false)

  const [visibleDetails, setVisibleDetails] = useState<StatementDetails>(StatementDetails.None)

  const showACHStatus = () => {
    if (loadingACH) {
      return
    }
    setLoadingACH(true)
    invoiceAPI
      .fetchACHStatus(invoice.id)
      .then((status) => {
        setLoadingACH(false)
        setACHStatus(status)
        setVisibleDetails(StatementDetails.ACHStatus)
      })
      .catch((_err) => setLoadingACH(false))
  }

  const showACHLog = () => {
    if (loadingACHLogs) {
      return
    }
    setLoadingACHLogs(true)
    invoiceAPI
      .fetchACHLog(invoice.id)
      .then((log) => {
        setLoadingACHLogs(false)
        setACHLog(log)
        setVisibleDetails(StatementDetails.ACHLogs)
      })
      .catch((_err) => setLoadingACHLogs(false))
  }

  const [totalCredits, totalDebits] = useMemo(() => {
    const allTotals = R.toPairs(props.details.group_by_billing_rules)
      .filter(([_key, invoiceTotal]: [string, InvoiceTotals]) => invoiceTotal.totals.total != 0)
      .map(([_key, invoiceTotal]: [string, InvoiceTotals]) => invoiceTotal)

    return [
      allTotals.reduce((sum, invoiceTotal) => sum + invoiceTotal.totals.credit, 0),
      allTotals.reduce((sum, invoiceTotal) => sum + invoiceTotal.totals.debit, 0),
    ]
  }, [invoice])

  const renderCreditDebitTotalsSummary = () => (
    <table className="table table-condensed table-striped">
      <thead>
        <tr>
          <th>Summary</th>
          <th align="right">Amount</th>
        </tr>
      </thead>
      <tbody>
        {totalCredits != 0 && (
          <tr className={'info'}>
            <td>
              Total Credits{' '}
              <Button size="sm" color="info" onClick={() => setVisibleDetails(StatementDetails.Credits)}>
                <Icon name="info" />
              </Button>{' '}
            </td>
            <td align="right" onClick={() => setVisibleDetails(StatementDetails.Credits)}>
              {formatCents(totalCredits)}
            </td>
          </tr>
        )}
        {totalDebits != 0 && (
          <tr className="info">
            <td>
              Total Debits{' '}
              <Button size="sm" color="info" onClick={() => setVisibleDetails(StatementDetails.Debits)}>
                <Icon name="info" />
              </Button>
            </td>
            <td align="right" onClick={() => setVisibleDetails(StatementDetails.Debits)}>
              {formatCents(totalDebits)}
            </td>
          </tr>
        )}
        <tr className={invoice.total > 0 ? 'info' : 'danger'}>
          <td>{invoice.total > 0 ? 'Amount to Receive' : 'Amount to Pay'}</td>
          <td align="right">{formatCents(Math.abs(invoice.total))}</td>
        </tr>
      </tbody>
    </table>
  )

  const renderACHModal = () => {
    if (visibleDetails == StatementDetails.ACHStatus) {
      return (
        <Modal isOpen={true} onClosed={() => setVisibleDetails(StatementDetails.None)}>
          <ModalHeader>ACH Status Information</ModalHeader>
          <ModalBody>
            <Panel id="ach_status" label="ACH Status Information">
              <Line label="Status">{achStatus?.status}</Line>
              <Line label="Transfer Amount">{achStatus?.final_charged_amount}</Line>
              <Line label="Transaction ID">{achStatus?.transaction_id}</Line>
              <Line label="Transaction Type">{achStatus?.transaction_type}</Line>
              <Line label="Transaction Date">{achStatus?.transaction_date}</Line>
              <Line label="Bank Token">{achStatus?.bank_token}</Line>
            </Panel>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setVisibleDetails(StatementDetails.None)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )
    } else return <></>
  }

  const TS_FORMAT = 'MM/DD hh:mm:ss a'
  const ACH_TRANSFER_COMP = {
    all: (props: { log: ACHTransferLog }) => (
      <tr>
        <td>
          <strong>{props.log.method}</strong>
          <br />
          {moment(props.log.inserted_at).format(TS_FORMAT)}
        </td>
        <td>
          <span className="text-info">-</span>
          <br />
          <span className="text-success">
            {props.log.response.cabbage_return_code} - {props.log.response.message}
          </span>
        </td>
      </tr>
    ),
    move_money: (props: { log: ACHTransferLog }) => (
      <tr>
        <td>
          <strong>{props.log.method}</strong>
          <br />
          {moment(props.log.inserted_at).format(TS_FORMAT)}
        </td>
        <td>
          <span className="text-info">
            {props.log.request.transaction_type} ${props.log.request.final_amount}{' '}
            {props.log.request.statement_descriptor}
          </span>
          <br />
          <span className="text-success">
            {props.log.response.cabbage_return_code} - {props.log.response.message}
          </span>
        </td>
      </tr>
    ),
    check_token: (props: { log: ACHTransferLog }) => (
      <tr>
        <td>
          <strong>{props.log.method}</strong>
          <br />
          {moment(props.log.inserted_at).format(TS_FORMAT)}
        </td>
        <td>
          <span className="text-info">{props.log.request.bank_token}</span>
          <br />
          <span className="text-success">
            {props.log.response.cabbage_return_code} - {props.log.response.message}
          </span>
        </td>
      </tr>
    ),
    cabbage_webhook: (props: { log: ACHTransferLog }) => (
      <tr>
        <td>
          <strong>{props.log.method}</strong>
          <br />
          {moment(props.log.inserted_at).format(TS_FORMAT)}
        </td>
        <td>
          <span className="text-info">webhook</span>
          <br />
          <span className="text-success">
            {props.log.response.topic} - {moment(props.log.response.timestamp).format('LLL')}
          </span>
        </td>
      </tr>
    ),
  }

  const renderACHLogs = () => {
    if (visibleDetails == StatementDetails.ACHLogs) {
      return (
        <Modal isOpen={true} onClosed={() => setVisibleDetails(StatementDetails.None)}>
          <ModalHeader>ACH Transfer Logs</ModalHeader>
          <ModalBody>
            <Panel id="ach_logs" label="ACH Transfer Logs">
              <Line2>
                <table className="table table-striped table-condensed">
                  {achLog?.ach_transfers.map((log: ACHTransferLog) => {
                    const Comp: React.FC<{ log: ACHTransferLog }> =
                      ACH_TRANSFER_COMP[log.method] || ACH_TRANSFER_COMP['all']
                    return <Comp log={log} />
                  })}
                </table>
              </Line2>
            </Panel>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setVisibleDetails(StatementDetails.None)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )
    } else return <></>
  }

  const renderCreditModal = () => {
    if (visibleDetails == StatementDetails.Credits) {
      const allTotals = R.toPairs(props.details.group_by_billing_rules).filter(
        ([_key, invoiceTotal]: [string, InvoiceTotals]) => invoiceTotal.totals.credit != 0
      )
      return (
        <Modal isOpen={true} onClosed={() => setVisibleDetails(StatementDetails.None)}>
          <ModalHeader>Credits</ModalHeader>
          <ModalBody>
            <Panel id="credits" label="Credits">
              {allTotals.map(([key, invoiceTotal]) => (
                <Line key={key} label={key}>
                  {formatCents(invoiceTotal.totals.credit)}
                </Line>
              ))}
            </Panel>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setVisibleDetails(StatementDetails.None)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )
    } else return <></>
  }

  const renderDebitModal = () => {
    if (visibleDetails == StatementDetails.Debits) {
      const allTotals = R.toPairs(props.details.group_by_billing_rules).filter(
        ([_key, invoiceTotal]: [string, InvoiceTotals]) => invoiceTotal.totals.debit != 0
      )
      return (
        <Modal isOpen={true} onClosed={() => setVisibleDetails(StatementDetails.None)}>
          <ModalHeader>Debits</ModalHeader>
          <ModalBody>
            <Panel id="debits" label="Debits">
              {allTotals.map(([key, invoiceTotal]) => (
                <Line key={key} label={key}>
                  {formatCents(invoiceTotal.totals.debit)}
                </Line>
              ))}
            </Panel>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setVisibleDetails(StatementDetails.None)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      )
    } else return <></>
  }

  const [isBS, setIsBS] = useState(false)
  useEffect(() => {
    AccessGroupApis.isAuthorized('billing_supervisor').then(({ is_authorized }) => setIsBS(is_authorized))
  }, [])

  return (
    <>
      <Panel id="invoice" label={'Statement info'}>
        <Line label="Status">
          {invoice.status} {invoice.failed_reason && `(${invoice.failed_reason})`}
          {isBS && invoice.status.startsWith('ach_') && (
            <Button color={'info'} size="sm" onClick={showACHStatus}>
              {loadingACH ? '...fetching status' : 'Check Transfer Status'}
            </Button>
          )}
          {isBS && invoice.status.startsWith('ach_') && (
            <Button color={'info'} size="sm" onClick={showACHLog}>
              {loadingACHLogs ? '...fetching logs' : 'Transfer Logs'}
            </Button>
          )}
        </Line>

        <Line label="Period">
          {moment(invoice.start_time).format('LLL')} to {moment(invoice.end_time).format('LLL')}
        </Line>
        <li className="list-group-item">
          <div className="row">
            <div className="col-12">{renderCreditDebitTotalsSummary()}</div>

            {!props.simplifiedInvoice && (
              <div className="col-12">
                <table className="table table-condensed table-stripped">
                  <thead>
                    <tr>
                      <th>Statistics</th>
                      <th>Count</th>
                      <th align="right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {R.toPairs(details.group_by_payload_type).map(([key, invoiceTotal]: [string, InvoiceTotals]) => (
                      <tr key={key}>
                        <td>{key}</td>
                        <td>{invoiceTotal.count}</td>
                        <td align="right">{formatCents(invoiceTotal.totals.total)}</td>
                      </tr>
                    ))}
                    <tr className={invoice.total > 0 ? 'info' : 'danger'}>
                      <td>Total Orders & Amount</td>
                      <td>{invoice.billables_count || '??'}</td>
                      <td align="right">{formatCents(invoice.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </li>
      </Panel>
      {renderACHModal()}
      {renderACHLogs()}
      {renderCreditModal()}
      {renderDebitModal()}
    </>
  )
}

function OrderTotalsSummary(props: {
  gross_amount: number
  deliveries_sum: number
  subtotals_sum: number
  services_sum: number
  taxes_sum: number
  tips_sum: number
  simplifiedInvoice: boolean
}): JSX.Element {
  const row = (label: string, value: number) =>
    value != 0 ? (
      <tr style={{ color: '#777' }}>
        <td>
          <span style={{ marginLeft: '10px' }}>{label}</span>
        </td>
        <td align="right">{formatCents(value, 'en')}</td>
      </tr>
    ) : (
      <></>
    )

  return (
    <>
      {row('Total (before tax, fees, tips & charges)', props.subtotals_sum)}
      {row('Sales Tax', props.taxes_sum)}
      {!props.simplifiedInvoice && row('Gross Sales', props.gross_amount - props.taxes_sum)}
      {!props.simplifiedInvoice && row('Delivery Fee paid by customer', props.deliveries_sum)}
      {!props.simplifiedInvoice && row('Tips', props.tips_sum)}
      {!props.simplifiedInvoice && row('Service Fee paid by customer', props.services_sum)}
    </>
  )
}

function BillingRulesSummary(props: { details: InvoiceDetails }): JSX.Element {
  const allTotals = R.toPairs(props.details.group_by_billing_rules).filter(
    ([_key, invoiceTotal]: [string, InvoiceTotals]) => invoiceTotal.totals.total != 0
  )
  const positiveTotals = allTotals.filter(([_, invoiceTotal]) => invoiceTotal.totals.total > 0)
  const negativeTotals = allTotals.filter(([_, invoiceTotal]) => invoiceTotal.totals.total < 0)
  return (
    <>
      {[...positiveTotals, ...negativeTotals].map(([key, invoiceTotal]: [string, InvoiceTotals]) => (
        <tr key={key}>
          <td>{key}</td>
          <td align="right">{formatCents(invoiceTotal.totals.total, 'en')}</td>
        </tr>
      ))}
    </>
  )
}
export default withRouter(InvoiceDetailsUI)
